import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { ContentProvider, useContent } from "@/context/ContentContext";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AssistantWidget from "@/components/AssistantWidget";
import HomePage from "@/pages/HomePage";
import PalinsestoPage from "@/pages/PalinsestoPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function SiteLoader() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="site-loader">
      <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#007AFF] animate-spin" />
    </div>
  );
}

function Shell() {
  const { pathname } = useLocation();
  const { loading } = useContent();
  const isAdmin = pathname.startsWith("/admin");

  if (loading) return <SiteLoader />;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {!isAdmin && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/palinsesto-ai" element={<PalinsestoPage />} />
        <Route path="/admin" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isAdmin && pathname !== "/palinsesto-ai" && <Footer />}
      {(pathname === "/" || pathname === "/palinsesto-ai") && (
        <div className={pathname === "/palinsesto-ai" ? "palinsesto-chat" : ""}>
          <AssistantWidget />
        </div>
      )}
    </div>
  );
}

function LenisManager() {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let frame;
    const raf = (time) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(frame);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!lenisRef.current) return;
    if (pathname === "/palinsesto-ai") {
      lenisRef.current.stop();
    } else {
      lenisRef.current.start();
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ContentProvider>
            <ScrollToTop />
            <LenisManager />
            <Shell />
            <Toaster theme="dark" position="bottom-right" richColors />
          </ContentProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
